import { createSignal, onCleanup } from "solid-js";
import { clientRepo } from "~/server/apis/client_repo";
import { showSnackBar } from "~/shared_states/snackbar";
import { useWebStoreContext } from "~/context/webstore_context";
import { OrderDetails, VoucherDetails } from "~/server/types/order";
import { ViewData } from "../data/view_data";

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_ERRORS = 3;
const GENERIC_FAILURE_MSG = "Voucher generation failed. Please try again.";

export function useView(viewData: ViewData) {
  const { storeState } = useWebStoreContext();

  const [voucher, setVoucher] = createSignal<VoucherDetails | null>(
    viewData.voucher
  );
  const [isGenerating, setIsGenerating] = createSignal(false);

  let pollInterval: ReturnType<typeof setInterval> | undefined;

  const stopPolling = () => {
    if (pollInterval) {
      clearInterval(pollInterval);
      pollInterval = undefined;
    }
  };

  onCleanup(stopPolling);

  const failGeneration = (msg: string) => {
    stopPolling();
    setIsGenerating(false);
    showSnackBar({ level: "error", message: msg });
  };

  const handleOrderUpdate = (order: OrderDetails) => {
    if (order.status === "COMPLETED") {
      stopPolling();
      const v = order.orderProductDetails.vouchers?.[0];
      if (!v) {
        failGeneration(GENERIC_FAILURE_MSG);
        return;
      }
      setVoucher(v);
      setIsGenerating(false);
      return;
    }
    if (
      order.status === "FAILED" ||
      order.status === "REVERSED" ||
      order.status === "CANCELLED"
    ) {
      failGeneration(GENERIC_FAILURE_MSG);
    }
  };

  const pollOrder = (orderId: string) => {
    let errorCount = 0;
    const tick = async () => {
      try {
        const order = await clientRepo.getStoreOrderDetails(orderId);
        errorCount = 0;
        handleOrderUpdate(order);
      } catch (err) {
        console.error("home_indriya: poll error", err);
        errorCount++;
        if (errorCount >= MAX_POLL_ERRORS) {
          failGeneration(GENERIC_FAILURE_MSG);
        }
      }
    };
    void tick();
    pollInterval = setInterval(tick, POLL_INTERVAL_MS);
  };

  const generateVoucher = async () => {
    if (isGenerating()) return;

    const product = viewData.product;
    if (!product) {
      showSnackBar({
        level: "error",
        message: "Voucher unavailable. Please try again later.",
      });
      return;
    }

    setIsGenerating(true);
    try {
      const res = await clientRepo.initiateRedemption({
        productId: product.id,
        amount: storeState.coins ?? 0,
        paymentMode: "UPI",
        consumeCoins: true,
        denominationDetails: [
          {
            denomination: storeState.coins ?? 0,
            quantity: 1,
          },
        ]
      });
      await new Promise((resolve) => setTimeout(resolve, 1000));
      pollOrder(res.storeOrderDetails.id);
    } catch (err) {
      console.error("home_indriya: initiateRedemption failed", err);
      failGeneration(GENERIC_FAILURE_MSG);
    }
  };

  return {
    viewData,
    voucher,
    isGenerating,
    generateVoucher,
  };
}
